import { useState } from 'react'
import { saveAs } from 'file-saver'
import req from '@sportninja/common/api/request'

interface DownloadArgs {
  url: string
  /** Saved as `<fileName>.csv`. */
  fileName: string
  query?: Record<string, unknown>
}

const errorMessage = (error: unknown): string =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof error.message === 'string' &&
  error.message
    ? error.message
    : 'Unknown error'

/**
 * Fetches a CSV export and saves it. This is `FileDownload`'s logic as a hook,
 * for callers whose trigger is not a single button — an item in a menu of
 * exports, say — so every export in the app shares one request and one error
 * treatment.
 */
export const useFileDownload = () => {
  'use memo'

  const [loading, setLoading] = useState(false)

  const download = async ({ url, fileName, query = {} }: DownloadArgs) => {
    try {
      setLoading(true)
      const response = await req(url, {
        method: 'GET',
        parseJSON: false,
        skipAutoContentType: true,
        headers: {
          Accept: 'text/csv',
        },
        query,
      })
      const blob = await response.blob()
      saveAs(blob, `${fileName}.csv`)
    } catch (error) {
      console.error(error)
      alert(
        `Error downloading file. Please contact SportNinja support. Info: ${errorMessage(
          error
        )}`
      )
    } finally {
      setLoading(false)
    }
  }

  return { download, loading }
}
